import { Component, Inject, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, Input, Output, EventEmitter, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSliderModule } from '@angular/material/slider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';
import { LayoutService } from '../../services/layout.service';
import { AuthService } from '../../services/auth.service';
import { TaskCatalogGet } from '../../models/task';
import { GoalTemplateItem } from '../../models/goal/goal-template-item.model';
import { TaskBase } from '../../models/task/task-base.model';
import { GoalCreate } from '../../models/goal/goal-create.model';
import { GoalCatalogGet } from '../../models/goal/goal-catalog-get.model';
import { GoalUpdate } from '../../models/goal/goal-update.model';
import { GoalGet } from '../../models/goal/goal-get.model';
import { ConfirmDialogComponent, ConfirmDialogData } from '../confirm-dialog/confirm-dialog.component';
import { SaveConfirmDialogComponent, SaveConfirmDialogData, SaveConfirmDialogResult } from '../save-confirm-dialog/save-confirm-dialog.component';

export interface GoalDialogData {
  title?: string;
  projectId: string; // Required projectId for goal creation
  mode?: 'create' | 'edit'; // Dialog mode
  goalData?: GoalGet & { tasks?: TaskBase[] }; // Existing goal data for editing, with optional tasks
  schemaData?: { columns: { name: string; dataType: string }[] } | null; // Schema data for column filtering
  fullSchema?: any[]; // Full schema structure from infer-dtype for suggest-columns endpoint
  initialOrder?: number; // 1-based order for new goals
  existingGoalNames?: string[]; // Existing goal names for duplicate validation
}

export interface GoalFormData {
  goalType: string;
  name?: string;
  description?: string;
  parameters?: { [key: string]: any }; // Add parameters collection
  targetColumns?: string[]; // Add target columns for validation
  isActive?: boolean; // Goal active status
}

type DialogStage = 'goal-selection' | 'goal-configuration';

@Component({
  selector: 'app-select-goal-dialog',
  standalone: true,
  imports: [
    
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatExpansionModule,
    MatTooltipModule,
    MatSnackBarModule,
    FormsModule
  ],
  templateUrl: './select-goal-dialog.component.html',
  styleUrls: ['./select-goal-dialog.component.scss']
})
export class SelectGoalDialogComponent implements OnInit, OnChanges {
  // Input/Output for inline mode
  @Input() inlineData?: GoalDialogData;
  @Output() inlineSubmit = new EventEmitter<{ data: GoalFormData; goalTasks: any[] }>();
  @Output() inlineCancel = new EventEmitter<void>();
  @Output() titleChange = new EventEmitter<string>();
  @Output() subtitleChange = new EventEmitter<string>();
  
  // Get data from either inline input or dialog injection
  get effectiveData(): GoalDialogData {
    return this.inlineData || this.data || { projectId: '', mode: 'create' };
  }
  
  // Check if running in inline mode
  get isInlineMode(): boolean {
    return !!this.inlineData;
  }
  
  // List of tasks for this goal
  goalTasks: any[] = [];
  // Locked target columns once first task is added
  lockedTargetColumns: string[] = [];
  currentStage: DialogStage = 'goal-selection';
  
  // Task catalog with toggle state
  taskCatalogWithState: Array<TaskCatalogGet & { enabled: boolean; mandatory: boolean; required: boolean }> = [];
  // Currently selected task for viewing in right panel
  selectedTaskForView: (TaskCatalogGet & { enabled: boolean; mandatory: boolean; required: boolean }) | null = null;
  // Track which columns are selected for each task (taskId -> column[])
  taskColumnSelections: Map<string, string[]> = new Map();
  // Cache task parameters to prevent infinite loops (taskId -> parameters[])
  taskParametersCache: Map<string, Array<{ key: string; name?: string; value: any; default?: any; description?: string; type?: string; enum?: string[]; required?: boolean }>> = new Map();
  // Track collapsible section state
  columnSelectionExpanded = false;
  isEditMode = false; // Track if we're editing an existing goal
  // Pending goal data for edit mode (applied after task catalog loads)
  pendingGoalDataForEdit: (GoalGet & { tasks?: TaskBase[] }) | null = null;
  // Flag set while we are applying a pending saved goal configuration to the UI
  // and capturing the initial snapshot. While true, suppress unsaved-change prompts
  // as they would be false positives for UI-only restoration activity.
  private isApplyingPendingConfiguration = false;
  formData: GoalFormData = {
    goalType: '',
    name: '',
    description: '',
    parameters: {}, // Initialize parameters collection
    targetColumns: [], // Initialize target columns
    isActive: true // Default to active
  };

  // UI state
  showFavorites = false; // Start with All Items selected
  searchQuery = '';
  favoriteGoals = new Set<string>(); // Track favorite goal IDs (synced with API)
  loading = false;
  loadingFavorites = false; // Track loading state for favorite operations
  favoritesLoaded = false; // Track if favorites have been loaded from API
  error: string | null = null;
  advancedOptionsExpanded = false; // Track advanced options visibility
  expandedCategories = new Set<string>(); // Track which categories are expanded
  hasAttemptedCreate = false; // Track if user has attempted to create without selecting columns
  userPlanId: string | null = null; // User's plan ID for checking goal availability

  // Cache for performance optimization
  private _cachedRequiredFields: any[] | null = null;
  private _cachedAdvancedFields: any[] | null = null;
  private _lastSelectedTaskName: string | null = null;
  private _taskDisplayCache: Map<string, { name: string; description: string }> = new Map();
  
  // Track initial state for changes detection
  private initialTaskStates: string = '';
  private initialColumns: string[] = [];
  private initialParameterValues: Map<string, Map<string, any>> = new Map(); // taskId -> (paramKey -> paramValue)
  private initialSavePayload: string | null = null;
  // Toggle verbose change-detection logging during debugging
  // Set to true temporarily to capture payload diffs when unexpected prompts occur
  private debugChangeDetection = true;

  // Stable stringify helper to compare objects/arrays deterministically
  private stableStringify(value: any): string {
    return JSON.stringify(value, (_k, v) => {
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        return Object.keys(v).sort().reduce((acc: any, key: string) => { acc[key] = v[key]; return acc; }, {});
      }
      return v;
    });
  }

  // Deep clone helper using stable stringify to ensure snapshotting initial values
  private deepClone<T>(value: T): T {
    try {
      return JSON.parse(this.stableStringify(value));
    } catch (e) {
      // Fallback to returning the value itself if it can't be cloned
      return value as unknown as T;
    }
  }

  // Available categories from starter tasks
  availableCategories: string[] = [];
  goalTemplates: GoalTemplateItem[] = [];
  goalCatalog: GoalCatalogGet[] = []; // Catalog of available goals
  taskCatalog: TaskCatalogGet[] = []; // Tasks for the selected goal
  selectedGoalId: string | null = null; // Track selected goal ID
  loadingTasks = false; // Loading state for task catalog
  suggestedColumns: string[] = []; // Suggested columns from suggest-columns endpoint
  suggestedParams: Map<string, any> = new Map(); // Suggested parameters by task_id
  
  // Check if both favorites and starter tasks are ready
  get isDataReady(): boolean {
    return this.favoritesLoaded && !this.loading;
  }
  
  // Schema data from project data source
  get availableColumns(): string[] {
    if (!this.effectiveData.schemaData?.columns) {
      // Fallback to mock data if no schema is available
      return [
        'name', 'email', 'phone', 'address', 'city', 'country', 
        'age', 'salary', 'department', 'hire_date', 'status'
      ];
    }

    const selectedTask = this.getSelectedGoalTemplate();
    if (!selectedTask) {
      // Return all columns if no task is selected
      return this.effectiveData.schemaData.columns.map(col => col.name);
    }

    // Filter columns based on task requirements
    return this.getCompatibleColumns(selectedTask);
  }

  // Scroll state for gradients
  hasScrollTop = false;
  hasScrollBottom = false;

  private getCompatibleColumns(task: GoalTemplateItem): string[] {
    if (!this.effectiveData.schemaData?.columns) {
      return [];
    }

    // Get required data types for this task from input/output specifications
    const requiredDataTypes = this.getRequiredDataTypesForTask(task);
    
    if (requiredDataTypes.length === 0) {
      // If no specific data types are required, return all columns
      return this.effectiveData.schemaData.columns.map(col => col.name);
    }

    // Filter columns to only those with compatible data types
    return this.effectiveData.schemaData.columns
      .filter(col => this.isDataTypeCompatible(col.dataType, requiredDataTypes))
      .map(col => col.name);
  }

  private getRequiredDataTypesForTask(task: GoalTemplateItem): string[] {
    const requiredTypes: string[] = [];
    
    // Check input type requirements
    if (typeof task.metadata.input === 'string') {
      const inputType = task.metadata.input.toLowerCase();
      if (inputType.includes('number') || inputType.includes('numeric') || inputType.includes('integer')) {
        requiredTypes.push('number', 'integer', 'currency', 'numeric');
      } else if (inputType.includes('string') || inputType.includes('text') || inputType.includes('str')) {
        requiredTypes.push('string', 'text', 'str');
      } else if (inputType.includes('date') || inputType.includes('time')) {
        requiredTypes.push('date', 'datetime', 'timestamp');
      } else if (inputType.includes('bool')) {
        requiredTypes.push('boolean');
      }
    }

    // If we can't determine specific types, allow all
    return requiredTypes;
  }

  private isDataTypeCompatible(columnDataType: string, requiredDataTypes: string[]): boolean {
    const normalizedColumnType = columnDataType.toLowerCase();
    
    return requiredDataTypes.some(requiredType => {
      const normalizedRequiredType = requiredType.toLowerCase();
      
      // Exact match
      if (normalizedColumnType === normalizedRequiredType) {
        return true;
      }
      
      // Compatible type groups
      if ((normalizedRequiredType === 'number' || normalizedRequiredType === 'integer' || normalizedRequiredType === 'currency' || normalizedRequiredType === 'numeric') &&
          (normalizedColumnType === 'number' || normalizedColumnType === 'integer' || normalizedColumnType === 'currency' || normalizedColumnType === 'numeric' || normalizedColumnType === 'decimal' || normalizedColumnType === 'float')) {
        return true;
      }
      
      if ((normalizedRequiredType === 'string' || normalizedRequiredType === 'text') &&
          (normalizedColumnType === 'str' || normalizedColumnType === 'string' || normalizedColumnType === 'text' || normalizedColumnType === 'varchar' || normalizedColumnType === 'char')) {
        return true;
      }
      
      if ((normalizedRequiredType === 'date' || normalizedRequiredType === 'datetime' || normalizedRequiredType === 'timestamp') &&
          (normalizedColumnType === 'date' || normalizedColumnType === 'datetime' || normalizedColumnType === 'timestamp' || normalizedColumnType === 'time')) {
        return true;
      }
      
      if (normalizedRequiredType === 'boolean' && 
          (normalizedColumnType === 'boolean' || normalizedColumnType === 'bool' || normalizedColumnType === 'bit')) {
        return true;
      }
      
      return false;
    });
  }

  constructor(
    @Optional() public dialogRef: MatDialogRef<SelectGoalDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: GoalDialogData,
    private dataService: DataService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    public layoutService: LayoutService,
    private snackBar: MatSnackBar
  ) {
    // Get user plan ID for checking goal availability
    const currentUser = this.authService.getCurrentUser();
    this.userPlanId = currentUser?.plan?.planId || null;
  }

  ngOnInit(): void {
    this.initializeComponent();
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    // Reset component when inlineData changes (specifically when goalData or mode changes)
    if (changes['inlineData'] && !changes['inlineData'].firstChange) {
      const currentData = changes['inlineData'].currentValue as GoalDialogData;
      const previousData = changes['inlineData'].previousValue as GoalDialogData;
      
      // Check if the goal ID changed (including transitions to/from null/undefined)
      const currentGoalId = currentData?.goalData?.goalId;
      const previousGoalId = previousData?.goalData?.goalId;
      
      // Check if mode changed
      const currentMode = currentData?.mode;
      const previousMode = previousData?.mode;
      
      // Detect changes: ID change, mode change, OR goalData presence changed
      const goalIdChanged = currentGoalId !== previousGoalId;
      const modeChanged = currentMode !== previousMode;
      const goalDataPresenceChanged = (!!currentData?.goalData) !== (!!previousData?.goalData);
      
      // Always reset if there's any meaningful change
      if (goalIdChanged || modeChanged || goalDataPresenceChanged) {
        this.resetComponent();
        this.initializeComponent();
      }
    }
  }
  
  private resetComponent(): void {
    // Reset all component state
    this.currentStage = 'goal-selection';
    this.goalTasks = [];
    this.lockedTargetColumns = [];
    this.formData = {
      goalType: '',
      name: '',
      description: '',
      parameters: {},
      targetColumns: [],
      isActive: true
    };
    this.showFavorites = false;
    this.searchQuery = '';
    this.error = null;
    this.isEditMode = false;
    this.initialTaskStates = '';
    this.initialColumns = [];
    this.initialParameterValues.clear();
    this.suggestedColumns = [];
    this.suggestedParams.clear();
    this.taskParametersCache.clear();
  }
  
  private initializeComponent(): void {
    // Check if we're in edit mode
    this.isEditMode = this.effectiveData.mode === 'edit';
    
    // Load favorites first - this will trigger loadGoalTemplates() when done
    this.loadFavoriteGoalTemplates();
    
    // Note: loadGoalTemplates() is called by loadFavoriteGoalTemplates() after favorites are loaded
    // This ensures favoritesLoaded is true when goals are displayed
    
    // Emit initial title for inline mode
    if (this.isInlineMode) {
      this.emitTitle();
    }
  }
  
  private emitTitle(): void {
    if (this.isInlineMode) {
      this.titleChange.emit(this.getDialogTitle());
      this.subtitleChange.emit(this.getDialogSubtitle());
    }
  }

  loadFavoriteGoalTemplates(): void {
    this.loadingFavorites = true;
    this.favoritesLoaded = false;
    this.dataService.getFavoriteTasks().subscribe({
      next: (taskNames) => {
        // Handle both string array and object array responses
        let taskNameStrings: string[];
        if (Array.isArray(taskNames) && taskNames.length > 0) {
          // Check if first item is an object
          if (typeof taskNames[0] === 'object' && taskNames[0] !== null) {
            // Try common property names for task names
            taskNameStrings = taskNames.map((item: any) => 
              item.taskName || item.TaskName || item.name || item.Name || String(item)
            );
          } else {
            // Already strings
            taskNameStrings = taskNames as string[];
          }
        } else {
          taskNameStrings = [];
        }
        
        this.favoriteGoals = new Set(taskNameStrings);
        this.loadingFavorites = false;
        this.favoritesLoaded = true;
        
        // After favorites are loaded, load starter tasks based on mode
        if (this.isEditMode && this.effectiveData.goalData) {
          this.loadGoalTemplatesForEdit(this.effectiveData.goalData);
        } else {
          this.loadGoalTemplates();
        }
      },
      error: (error) => {
        console.error('Error loading favorite tasks:', error);
        this.loadingFavorites = false;
        this.favoritesLoaded = true; // Mark as loaded even on error so we don't block UI
        // Continue with empty set if loading fails, but still load starter tasks
        this.favoriteGoals = new Set<string>();
        
        // After favorites error, still load starter tasks based on mode
        if (this.isEditMode && this.effectiveData.goalData) {
          this.loadGoalTemplatesForEdit(this.effectiveData.goalData);
        } else {
          this.loadGoalTemplates();
        }
      }
    });
  }

  /**
   * Parse tags from a goal's tags field (JSON array string)
   */
  private parseGoalTags(goal: GoalCatalogGet): string[] {
    const tagsField = goal.tags || (goal as any).Tags;
    if (!tagsField) {
      return ['General']; // Default tag if none provided
    }
    
    try {
      const parsed = JSON.parse(tagsField);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map(tag => String(tag).trim()).filter(tag => tag.length > 0);
      }
    } catch (e) {
      console.warn(`Failed to parse tags for goal ${goal.goalKey}:`, e);
    }
    
    return ['General']; // Default tag if parsing fails
  }

  loadGoalTemplates(): void {
    this.loading = true;
    this.error = null;

    // Load goal catalog from API - this is now the primary source
    this.dataService.getGoalCatalog().subscribe({
      next: (catalog) => {
        // Use the catalog returned from the API directly
        const combinedCatalog = Array.isArray(catalog) ? catalog : [];
        this.goalCatalog = combinedCatalog;

        // Map goal catalog to starter tasks format for backward compatibility
        this.goalTemplates = combinedCatalog
          .filter(goal => goal.isActive ?? (goal as any).IsActive ?? false)
          .map(goal => ({
            key: goal.goalKey || (goal as any).GoalKey,
            metadata: {
              name: goal.name || (goal as any).Name,
              description: goal.simpleDescription || goal.advancedDescription || '',
              simpleDescription: goal.simpleDescription || '',
              advancedDescription: goal.advancedDescription || '',
              category: goal.category || (goal as any).Category || 'General',
              tags: this.parseGoalTags(goal),
              complexity: (goal.complexity ?? (goal as any).Complexity)?.toString() || 'Medium',
              requiredColumns: (goal.parameters || (goal as any).Parameters)?.requiredColumns || [],
              optionalColumns: (goal.parameters || (goal as any).Parameters)?.optionalColumns || [],
              parameters: (goal.parameters || (goal as any).Parameters) || {}
            },
            isFavorite: this.favoriteGoals.has(goal.goalKey || (goal as any).GoalKey),
            isFeatured: (goal.isFeatured ?? (goal as any).IsFeatured) === true,
            isPreview: (goal.isPreview ?? (goal as any).IsPreview) === true
          }));

        this.extractUniqueCategories();
        // Expand all categories by default
        this.expandedCategories = new Set(this.availableCategories);
        
        // If in edit mode, find and select the matching task
        if (this.isEditMode && this.formData.goalType) {
          this.findAndSelectGoalTemplateForEdit();
        }
        
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading goal catalog:', error);
        this.error = 'Failed to load available goals';
        this.loading = false;
      }
    });
  }
  
  /**
   * Load starter tasks for edit mode with proper sequencing
   */
  loadGoalTemplatesForEdit(goalData: GoalGet & { tasks?: TaskBase[] }): void {
    console.log('=== loadGoalTemplatesForEdit called ===');
    console.log('goalData received:', goalData);
    
    // Handle both camelCase and PascalCase properties from API
    const isActive = goalData.isActive ?? (goalData as any).IsActive;
    console.log('goalData.isActive:', isActive, 'type:', typeof isActive);
    
    this.loading = true;
    this.error = null;

    // Ensure we have a goalId (handle both cases)
    const goalId = goalData.goalId || (goalData as any).GoalId;
    if (!goalId) {
      console.error('Cannot load tasks: goalId is missing');
      this.error = 'Invalid goal data';
      this.loading = false;
      return;
    }

    // Step 1: Created goal is already loaded (passed as parameter)
    // Step 1.5: Fetch the created tasks with taskJson from Task/list endpoint
    this.dataService.getTaskList(goalId).subscribe({
      next: (createdTasks) => {
        console.log('Loaded created tasks with taskJson:', createdTasks);
        
        // Store created tasks in the goal data
        const goalDataWithTasks = {
          ...goalData,
          tasks: createdTasks
        };
        
        // Step 2: Load the Goal Catalog
        this.dataService.getGoalCatalog().subscribe({
          next: (catalog) => {
            // Use the catalog returned from the API directly in edit mode
            const combinedCatalog = Array.isArray(catalog) ? catalog : [];
            this.goalCatalog = combinedCatalog;
            
                // Map goal catalog to starter tasks format
                this.goalTemplates = combinedCatalog
              .filter(goal => {
                const isActive = goal.isActive ?? (goal as any).IsActive ?? false;
                return isActive;
              })
              .map(goal => {
                // Handle both camelCase and PascalCase properties from API
                const goalKey = goal.goalKey || (goal as any).GoalKey;
                const name = goal.name || (goal as any).Name;
                const simpleDescription = goal.simpleDescription || (goal as any).SimpleDescription;
                const advancedDescription = goal.advancedDescription || (goal as any).AdvancedDescription;
                const category = goal.category || (goal as any).Category;
                const parameters = goal.parameters || (goal as any).Parameters;
                const complexity = goal.complexity ?? (goal as any).Complexity;
                
                let parsedParams: any = {};
                try {
                  if (parameters) {
                    parsedParams = JSON.parse(parameters);
                  }
                } catch (e) {
                  console.warn(`Failed to parse parameters for goal ${goalKey}:`, e);
                }

                // Parse tags from the tags field
                const tags = this.parseGoalTags(goal);

                return {
                  key: goalKey,
                  metadata: {
                    name: name,
                    description: simpleDescription || advancedDescription || '',
                    simpleDescription: simpleDescription || '',
                    advancedDescription: advancedDescription || '',
                    category: category || 'General',
                    tags: tags,
                    complexity: complexity?.toString() || 'Medium',
                    requiredColumns: parsedParams.requiredColumns || [],
                    optionalColumns: parsedParams.optionalColumns || [],
                    parameters: parsedParams.parameters || {},
                    ...parsedParams
                  },
                  isFavorite: this.favoriteGoals.has(goalKey),
                  isFeatured: (goal.isFeatured ?? (goal as any).IsFeatured) === true,
                  isPreview: (goal.isPreview ?? (goal as any).IsPreview) === true
                } as GoalTemplateItem;
              });
            
            this.extractUniqueCategories();
            this.expandedCategories = new Set(this.availableCategories);
            
            // Step 3: Find the matching Goal by catalog ID (preferred) or fall back to name
            let matchingGoal: GoalTemplateItem | undefined;
            
            // Handle both camelCase and PascalCase property names from API
            const catalogId = goalData.goalCatalogId || (goalData as any).GoalCatalogId;
            
            if (catalogId) {
              // Try to find by catalog ID first (more reliable)
              console.log('Looking for goal with catalogId:', catalogId);
              console.log('Available catalog goals:', this.goalCatalog.map(g => ({ 
                goalId: g.goalId || (g as any).GoalId, 
                goalKey: g.goalKey || (g as any).GoalKey 
              })));
              
              const catalogGoal = this.goalCatalog.find(g => {
                const gId = g.goalId || (g as any).GoalId;
                return gId?.toString().toLowerCase() === catalogId?.toString().toLowerCase();
              });
              if (catalogGoal) {
                const goalKey = catalogGoal.goalKey || (catalogGoal as any).GoalKey;
                matchingGoal = this.goalTemplates.find(st => st.key === goalKey);
                console.log('Matched goal by catalog ID:', goalKey);
              } else {
                console.warn('No catalog goal found with goalId matching:', catalogId);
              }
            }
            
            // Fall back to name matching if no catalog ID or not found
            if (!matchingGoal) {
              const goalName = goalData.goalName || (goalData as any).GoalName;
              matchingGoal = this.goalTemplates.find(st => 
                st.metadata.name === goalName
              );
              if (matchingGoal) {
                console.log('Matched goal by name (fallback):', goalName);
              }
            }
            
            if (matchingGoal) {
              // Select the goal (this will load task catalog and set up Step 2)
              this.selectGoalTemplate(matchingGoal);
              
              // Store goal data with created tasks for restoration after task catalog loads
              this.pendingGoalDataForEdit = goalDataWithTasks;
              // Indicate we are applying the saved configuration so change-detection
              // can ignore transient differences until the snapshot is captured.
              this.isApplyingPendingConfiguration = true;
              // Immediately show the configuration UI so the user is taken to step 2
              // while we apply the pending configuration and later capture the snapshot.
              this.showConfigurationStageOnly();
              
              // Defer moving to configuration stage until pending configuration is applied
              // The pending configuration will call `captureInitialState()` and then set
              // `currentStage` so external callers won't check for unsaved changes
              // before the initial snapshot is available.
              // this.currentStage will be set after applyGoalConfigurationToTasks().
            } else {
              const goalName = goalData.goalName || (goalData as any).GoalName;
              console.warn('No matching goal found in catalog for:', goalName, 'with catalogId:', catalogId);
              this.error = 'Could not find matching goal template';
            }
            
            this.loading = false;
            this.cdr.detectChanges();
          },
          error: (error) => {
            console.error('Error loading goal catalog for edit:', error);
            this.error = 'Failed to load goal catalog';
            this.loading = false;
          }
        });
      },
      error: (error) => {
        console.error('Error loading created tasks for edit:', error);
        this.error = 'Failed to load task details';
        this.loading = false;
      }
    });
  }

  private findAndSelectGoalTemplateForEdit(): void {
    if (!this.formData.goalType || !this.goalTemplates.length) {
      console.warn('Cannot find goal template for edit: missing goalType or goalTemplates', {
        hasGoalType: !!this.formData.goalType,
        goalTemplatesCount: this.goalTemplates.length
      });
      return;
    }

    // Find the matching goal template by TaskName
    const matchingGoalTemplate = this.goalTemplates.find(task => task.key === this.formData.goalType);
    
    if (matchingGoalTemplate) {
      console.log('Found matching goal template for edit mode:', matchingGoalTemplate.key, matchingGoalTemplate.metadata.name);
      
      // Store the original user data before calling selectGoalTemplate
      const originalData = {
        name: this.formData.name,
        description: this.formData.description,
        targetColumns: [...(this.formData.targetColumns || [])],
        parameters: { ...(this.formData.parameters || {}) }
      };
      
      // Call selectGoalTemplate to set up the goal template metadata and UI state
      // This will populate example data and metadata fields
      this.selectGoalTemplate(matchingGoalTemplate);
      
      // Now restore the user's original data while preserving the goal template setup
      this.formData.name = originalData.name;
      this.formData.description = originalData.description;
      
      // Filter target columns through the new goal template's compatibility
      this.formData.targetColumns = originalData.targetColumns.filter(column => 
        this.availableColumns.includes(column)
      );
      
      // Merge user parameters with goal template defaults, giving priority to user values
      if (originalData.parameters && Object.keys(originalData.parameters).length > 0) {
        this.formData.parameters = { 
          ...(this.formData.parameters || {}), // Goal template defaults
          ...originalData.parameters // User values override defaults
        };
      }
      
      // Force a refresh of cached metadata fields to ensure UI updates
      this.clearFieldCache();
      
      // Use setTimeout to ensure the change detection cycle completes
      setTimeout(() => {
        this.cdr.detectChanges();
        
        console.log('Successfully matched and selected goal template for edit:', {
          templateKey: matchingGoalTemplate.key,
          targetColumns: this.formData.targetColumns,
          availableColumns: this.availableColumns.length,
          parameters: Object.keys(this.formData.parameters || {}).length,
          hasExampleData: this.hasExampleData(),
          selectedGoalTemplate: !!this.getSelectedGoalTemplate(),
          exampleDataContent: matchingGoalTemplate.metadata?.example
        });
      }, 0);
    } else {
      console.warn('No matching goal template found for goalType:', this.formData.goalType);
      console.log('Available goal templates:', this.goalTemplates.map(t => t.key));
      console.log('Looking for goal template with goalType:', this.formData.goalType);
      
      // Try a case-insensitive match as a fallback
      const caseInsensitiveMatch = this.goalTemplates.find(task => 
        task.key.toLowerCase() === this.formData.goalType.toLowerCase()
      );
      
      if (caseInsensitiveMatch) {
        console.log('Found case-insensitive match, using:', caseInsensitiveMatch.key);
        // Update the formData.goalType to match the correct case
        this.formData.goalType = caseInsensitiveMatch.key;
        // Retry with the corrected case
        this.findAndSelectGoalTemplateForEdit();
        return;
      }
      
      // Even if we can't find the exact task, the form should still work with the existing data
    }
  }

  private extractUniqueCategories(): void {
    const categories = new Set<string>();
    
    this.goalTemplates.forEach(task => {
      if (task.metadata.category?.trim()) {
        categories.add(task.metadata.category.trim());
      }
    });

    // Build sorted list but pin Featured to the top if any templates are featured
    const sorted = Array.from(categories).sort();
    const hasFeatured = this.goalTemplates.some(t => t.isFeatured === true);
    if (hasFeatured) {
      // Ensure 'Featured' appears first
      this.availableCategories = ['Featured', ...sorted.filter(c => c !== 'Featured')];
    } else {
      this.availableCategories = sorted;
    }
  }

  get filteredGoalTemplates(): GoalTemplateItem[] {
    let tasks = this.goalTemplates;

    // Filter by favorites if showing favorites
    if (this.showFavorites) {
      tasks = tasks.filter(task => task.isFavorite === true);
    }

    // Filter by search query
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase().trim();
      tasks = tasks.filter(task => {
        // Search in name and key
        if (task.key.toLowerCase().includes(query) ||
            task.metadata.name.toLowerCase().includes(query)) {
          return true;
        }
        
        // Search in tags
        const tags = task.metadata.tags || [];
        if (Array.isArray(tags) && tags.some(tag => tag.toLowerCase().includes(query))) {
          return true;
        }
        
        return false;
      });
    }

    return tasks;
  }

  get filteredCategories(): string[] {
    let categories = this.availableCategories;
    
    // Filter by search query first to get relevant categories
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase().trim();
      categories = categories.filter(category => {
        // Check if category name matches
        if (category.toLowerCase().includes(query) || 
            this.formatCategoryTitle(category).toLowerCase().includes(query)) {
          return true;
        }
        
        // Check if any tasks in this category match the search
        const tasksInCategory = this.goalTemplates.filter(task => task.metadata.category === category);
        return tasksInCategory.some(task => 
          task.key.toLowerCase().includes(query) ||
          task.metadata.name.toLowerCase().includes(query) ||
          (task.metadata.tags || []).some(tag => tag.toLowerCase().includes(query))
        );
      });
    }

    // Filter by favorites if showing favorites
    if (this.showFavorites) {
      categories = categories.filter(category => {
        const tasksInCategory = this.goalTemplates.filter(task => task.metadata.category === category);
        return tasksInCategory.some(task => task.isFavorite === true);
      });
    }

    // Filter out categories with no tasks after all filtering is applied
    categories = categories.filter(category => this.getGoalTemplatesForCategory(category).length > 0);

    return categories;
  }

  // Get tasks for a specific category with filtering applied
  getGoalTemplatesForCategory(category: string): GoalTemplateItem[] {
    let tasks: GoalTemplateItem[];

    // Special handling for the Featured pseudo-category
    if (category === 'Featured') {
      tasks = this.goalTemplates.filter(task => task.isFeatured === true);
    } else {
      tasks = this.goalTemplates.filter(task => task.metadata.category === category);
    }

    // Filter by favorites if showing favorites
    if (this.showFavorites) {
      tasks = tasks.filter(task => task.isFavorite === true);
    }

    // Filter by search query
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase().trim();
      tasks = tasks.filter(task => {
        if (task.key.toLowerCase().includes(query) ||
            task.metadata.name.toLowerCase().includes(query)) {
          return true;
        }
        const tags = task.metadata.tags || [];
        return Array.isArray(tags) && tags.some(tag => tag.toLowerCase().includes(query));
      });
    }

    return tasks;
  }

  selectGoalTemplate(task: GoalTemplateItem): void {
    // Check if goal is supported
    if (!this.isGoalSupported(task.key)) {
      // Show upgrade message
      this.snackBar.open(
        `${task.metadata.name} requires a plan upgrade`,
        '×',
        {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          duration: 3000,
          panelClass: ['info-snackbar']
        }
      );
      return; // Don't proceed with selection
    }

    this.formData.goalType = task.key;
    
    // Find the corresponding goal in the catalog to get the goalId
    const selectedGoal = this.goalCatalog.find(goal => {
      const goalKey = goal.goalKey || (goal as any).GoalKey;
      return goalKey === task.key;
    });
    if (selectedGoal) {
      const goalId = selectedGoal.goalId || (selectedGoal as any).GoalId;
      this.selectedGoalId = goalId;
      // Load tasks for this goal
      this.loadTasksForGoal(goalId);
    }
    
    // Auto-fill name and description based on task selection
    const baseName = task.metadata.name || this.formatCategoryTitle(task.metadata.category);
    this.formData.name = this.generateUniqueGoalName(baseName);
    this.formData.description = task.metadata.description || this.getCategoryDescription(task.metadata.category);
    
    // Initialize parameters with default values
    this.initializeParametersWithDefaults(task);
    
    // Clear cache when task changes
    this.clearFieldCache();
    
    // Filter available columns and update selected columns if necessary
    this.updateColumnsForTask(task);
  }

  /**
   * Show the configuration stage in the UI without capturing the initial snapshot.
   * Used when opening an existing goal so the user is taken directly to step 2
   * while we apply the saved configuration and then capture the snapshot.
   */
  private showConfigurationStageOnly(): void {
    if (this.debugChangeDetection) console.log('showConfigurationStageOnly: setting UI to goal-configuration without snapshot');
    this.currentStage = 'goal-configuration';
    this.emitTitle();
    // Ensure the UI updates immediately
    this.cdr.detectChanges();
  }

  /**
   * Load tasks for the selected goal from the API
   */
  loadTasksForGoal(goalId: string): void {
    this.loadingTasks = true;
    this.taskCatalog = [];
    this.taskCatalogWithState = [];
    this.selectedTaskForView = null;
    
    this.dataService.getTasksForGoal(goalId).subscribe({
      next: (tasks) => {
        // Sort tasks by taskOrder (include all tasks regardless of isActive)
        this.taskCatalog = tasks
          .sort((a, b) => (a.taskOrder || 0) - (b.taskOrder || 0));
        
        // Initialize task catalog with state
        this.taskCatalogWithState = this.taskCatalog.map(task => {
          const mandatory = this.isTaskMandatory(task);
          let requiredByDefault = false;
          try {
            if (task.taskMetadata) {
              const metadata = JSON.parse(task.taskMetadata);
              requiredByDefault = metadata.required === true;
            }
          } catch (e) {
            console.warn('Failed to parse taskMetadata for required flag:', e);
          }
          const initialEnabled = mandatory || requiredByDefault;
          
          // Use user-selected columns for all tasks (respecting user's choice)
          this.taskColumnSelections.set(task.taskId, [...(this.formData.targetColumns || [])]);
          
          // Pre-cache parameters to avoid repeated parsing
          this.getTaskParameters({ ...task, enabled: initialEnabled, mandatory: mandatory, required: requiredByDefault });
          return {
            ...task,
            enabled: initialEnabled, // Mandatory or required tasks enabled by default
            mandatory: mandatory,
            required: requiredByDefault
          };
        });
        
        // Don't auto-select first task - let user choose
        this.selectedTaskForView = null;
        
        this.loadingTasks = false;
        
        // Step 4 & 5: If in edit mode with pending goal data, apply task configurations
        if (this.isEditMode && this.pendingGoalDataForEdit) {
          console.log('=== About to apply pending goal data ===');
          console.log('pendingGoalDataForEdit:', this.pendingGoalDataForEdit);
          this.applyGoalConfigurationToTasks(this.pendingGoalDataForEdit);
          this.pendingGoalDataForEdit = null; // Clear after applying
          console.log('=== After applying, formData.isActive:', this.formData.isActive);
          
          // Fetch column suggestions for visual display (but don't auto-select)
          if (this.effectiveData.schemaData?.columns && this.formData.goalType) {
            this.fetchColumnSuggestions(false); // Don't auto-select for existing goals
          }
        }
        
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading tasks for goal:', error);
        this.taskCatalog = [];
        this.taskCatalogWithState = [];
        this.loadingTasks = false;
      }
    });
  }
  
  /**
   * Apply saved goal configuration to loaded task catalog (Steps 4 & 5 of edit flow)
   */
  private applyGoalConfigurationToTasks(goalData: GoalGet & { tasks?: TaskBase[] }): void {
    // Ensure we always clear the applying flag and capture a snapshot even if errors or early returns occur
    try {
      console.log('=== Applying goal configuration to tasks ===');
      console.log('goalData:', goalData);

      // Handle both camelCase and PascalCase properties from API
      const goalName = goalData.goalName || (goalData as any).GoalName;
      const description = goalData.description || (goalData as any).Description;
      const isActive = goalData.isActive ?? (goalData as any).IsActive;

      console.log('goalData.isActive value:', isActive, 'type:', typeof isActive);
      console.log('formData BEFORE update:', { ...this.formData });

      // Restore goal-level data
      this.formData.name = goalName;
      this.formData.description = description;
      this.formData.isActive = isActive;

      console.log('formData AFTER update:', { ...this.formData });
      console.log('Set formData.isActive to:', this.formData.isActive, 'from goalData.isActive:', isActive);

      // First, disable all non-mandatory tasks to ensure only tasks created for this goal are enabled
      this.taskCatalogWithState.forEach(catalogTask => {
        if (!catalogTask.mandatory) {
          catalogTask.enabled = false;
        }
      });

      // Parse task configurations and collect all columns used by any task
      const allUsedColumns = new Set<string>();
      const parsedTasks: Array<{ taskId: string; params: any; columns: string[] }> = [];

      if (!goalData.tasks || goalData.tasks.length === 0) {
        console.warn('No tasks to apply');
      } else {
        goalData.tasks.forEach(task => {
          try {
            console.log('Processing task:', task);

            // Parse taskJson if it exists
            let taskConfig: any = {};
            if ('taskJson' in task && (task as any).taskJson) {
              const taskJsonString = (task as any).taskJson;
              console.log('taskJson string:', taskJsonString);
              taskConfig = JSON.parse(taskJsonString);
              console.log('Parsed taskJson:', taskConfig);
              console.log('taskConfig.select:', taskConfig.select);
              console.log('taskConfig.select?.columns:', taskConfig.select?.columns);
            } else {
              console.log('No taskJson found in task');
            }

            // Extract task_id, params, and columns from parsed JSON
            const taskId = taskConfig.task_id || task.taskId;
            const params = taskConfig.params || task.parameters || {};
            const columns = taskConfig.select?.columns || (task as any).targetColumn || [];

            console.log(`Task ${taskId}: columns =`, columns, ', params =', params);
            console.log('columns type:', typeof columns, 'isArray:', Array.isArray(columns));

            // Store parsed task data
            parsedTasks.push({ taskId, params, columns });

            // Add columns to the union set
            if (Array.isArray(columns)) {
              columns.forEach((col: string) => {
                console.log('Adding column to set:', col);
                allUsedColumns.add(col);
              });
            }
          } catch (e) {
            console.warn('Failed to parse task configuration:', e, task);
          }
        });
      }

      // Set goal-level columns to the union of all task columns
      const goalColumns = Array.from(allUsedColumns);
      console.log('Setting goal-level columns:', goalColumns);
      this.formData.targetColumns = goalColumns;
      this.lockedTargetColumns = [...goalColumns];

      // Apply each parsed task's configuration to the catalog
      parsedTasks.forEach(({ taskId, params, columns }) => {
        // Find matching task in catalog by taskKey
        const catalogTask = this.taskCatalogWithState.find(ct => ct.taskKey === taskId);

        if (catalogTask) {
          console.log(`Enabling task ${taskId} with columns:`, columns);

          // Enable the task
          catalogTask.enabled = true;

          // Restore task-specific columns
          this.taskColumnSelections.set(catalogTask.taskId, columns);

          // Restore parameters - re-parse and update cached values
          if (params && Object.keys(params).length > 0) {
            // Force re-cache the parameters with restored values
            const taskWithState = { ...catalogTask, enabled: true, mandatory: catalogTask.mandatory };
            const parsedParams = this.getTaskParameters(taskWithState);

            // Update the parsed parameters with saved values
            parsedParams.forEach(param => {
              if (params[param.key] !== undefined) {
                param.value = params[param.key];
              }
            });

            // Re-cache with updated values
            this.taskParametersCache.set(catalogTask.taskId, parsedParams);
          }
        } else {
          console.warn(`Could not find catalog task for taskId: ${taskId}`);
        }
      });

      console.log('Final formData.targetColumns:', this.formData.targetColumns);

      // Select the first enabled task for viewing
      const firstEnabled = this.taskCatalogWithState.find(t => t.enabled);
      if (firstEnabled) {
        this.selectedTaskForView = firstEnabled;
      }

      // Force change detection to update UI
      this.cdr.detectChanges();
    } finally {
      // Always capture the snapshot and clear the applying flag so change detection works
      try {
        this.captureInitialState();
        if (this.debugChangeDetection) console.log('applyGoalConfigurationToTasks: captured initial state and clearing applying flag');
      } catch (e) {
        console.warn('applyGoalConfigurationToTasks: failed to capture initial state', e);
      }
      this.isApplyingPendingConfiguration = false;
    }
  }

  private updateColumnsForTask(task: GoalTemplateItem): void {
    // Get the current compatible columns for this task
    const compatibleColumns = this.getCompatibleColumns(task);
    
    // Remove any selected columns that are no longer compatible
    if (this.formData.targetColumns && this.formData.targetColumns.length > 0) {
      this.formData.targetColumns = this.formData.targetColumns.filter(column => 
        compatibleColumns.includes(column)
      );
    }
  }

  isColumnsFilteredByTask(): boolean {
    const selectedTask = this.getSelectedGoalTemplate();
    if (!selectedTask || !this.effectiveData.schemaData?.columns) {
      return false;
    }

    // Check if the available columns are fewer than total schema columns due to filtering
    const totalColumns = this.effectiveData.schemaData.columns.length;
    const availableColumns = this.availableColumns.length;
    
    return availableColumns < totalColumns;
  }

  getColumnDataType(columnName: string): string | null {
    if (!this.effectiveData.schemaData?.columns) {
      return null;
    }

    const column = this.effectiveData.schemaData.columns.find(col => col.name === columnName);
    return column ? column.dataType : null;
  }

  /**
   * Map raw data type to friendly display name
   */
  getFriendlyDataTypeName(dataType: string): string {
    if (!dataType) return '';
    
    const lowerType = dataType.toLowerCase();
    
    // Map common data types to friendly names
    const typeMap: { [key: string]: string } = {
      'numeric': 'Number',
      'str': 'Text',
      'string': 'Text',
      'bool': 'Bool',
      'boolean': 'Bool',
      'bit': 'Bool',
      'date': 'Date',
      'datetime': 'Date',
      'time': 'Time'
    };
    
    return typeMap[lowerType] || dataType;
  }

  private initializeParametersWithDefaults(task: GoalTemplateItem): void {
    this.formData.parameters = {};
    
    if (task.metadata.parameters) {
      Object.keys(task.metadata.parameters).forEach(paramKey => {
        const param = task.metadata.parameters![paramKey];
        if (param.default !== null && param.default !== undefined && param.default !== '') {
          this.formData.parameters![paramKey] = param.default;
        }
      });
    }
  }

  private clearFieldCache(): void {
    this._cachedRequiredFields = null;
    this._cachedAdvancedFields = null;
    this._lastSelectedTaskName = null;
  }

  private initializeForEdit(goalData: GoalGet & { tasks?: TaskBase[] }): void {
    // Handle both camelCase and PascalCase properties from API
    const goalName = goalData.goalName || (goalData as any).GoalName;
    const goalDescription = goalData.description || (goalData as any).Description;
    
    // Get targetColumn from the first task (targetColumn is now per task, not per goal)
    const firstTask = goalData.tasks?.[0];
    const taskTargetColumn = firstTask && 'targetColumn' in firstTask 
      ? (firstTask as any).targetColumn 
      : [];
    
    // Store task data for later restoration
    const existingTasks = goalData.tasks || [];
    
    // If we have a task ID, try to find the corresponding starter task in the catalog
    if (firstTask?.taskId) {
      // Wait for goal templates to load, then select the matching goal template
      // This will trigger the same flow as if the user clicked it in step 1
      const checkAndSelect = () => {
        if (this.goalTemplates.length > 0) {
          // Find the goal template that matches the first task's taskId
          const matchingGoalTemplate = this.goalTemplates.find(st => st.key === firstTask.taskId);
          
          if (matchingGoalTemplate) {
            // Select the goal template (this loads the task catalog, etc.)
            this.selectGoalTemplate(matchingGoalTemplate);
            
            // After selection, restore the goal-specific data
            setTimeout(() => {
              this.formData.name = goalName;
              this.formData.description = goalDescription;
              this.formData.targetColumns = taskTargetColumn;
              this.lockedTargetColumns = [...taskTargetColumn];
              
              // Restore task states and parameters from the existing goal
              this.restoreTaskStatesFromGoal(existingTasks, taskTargetColumn);
            }, 0);
          } else {
            // Fallback to old behavior if no matching goal template found
            this.initializeForEditFallback(goalName, goalDescription, taskTargetColumn, existingTasks);
          }
        } else {
          // Goal templates not loaded yet, try again
          setTimeout(checkAndSelect, 100);
        }
      };
      
      checkAndSelect();
    } else {
      // No task data, use fallback
      this.initializeForEditFallback(goalName, goalDescription, taskTargetColumn, existingTasks);
    }
  }
  
  /**
   * Restore task states from existing goal data
   */
  private restoreTaskStatesFromGoal(existingTasks: TaskBase[], goalColumns: string[]): void {
    if (!existingTasks.length) return;
    
    // For each task in the catalog, check if it exists in the goal's tasks
    existingTasks.forEach(existingTask => {
      const catalogTask = this.taskCatalogWithState.find(ct => ct.taskKey === existingTask.taskId);
      
      if (catalogTask) {
        // Enable the task
        catalogTask.enabled = true;
        
        // Restore task-specific columns
        const taskColumns = 'targetColumn' in existingTask 
          ? (existingTask as any).targetColumn 
          : goalColumns;
        this.taskColumnSelections.set(catalogTask.taskId, taskColumns);
        
        // Restore parameters
        if (existingTask.parameters) {
          const params = existingTask.parameters;
          // Get or create parameter cache for this task
          let cachedParams = this.taskParametersCache.get(catalogTask.taskId);
          if (cachedParams) {
            // Update values in cached parameters
            cachedParams.forEach(param => {
              if (params[param.key] !== undefined) {
                param.value = params[param.key];
              }
            });
          }
        }
      }
    });
    
    // Select the first enabled task for viewing
    const firstEnabled = this.taskCatalogWithState.find(t => t.enabled);
    if (firstEnabled) {
      this.selectedTaskForView = firstEnabled;
    }

    // Take an initial snapshot now that the saved configuration has been applied
    try {
      this.captureInitialState();
      if (this.debugChangeDetection) console.log('Captured initial state after applying saved goal configuration');
      // Now that the snapshot exists, mark the dialog as in configuration stage
      this.currentStage = 'goal-configuration';
      this.emitTitle();
      // Finished applying pending configuration
      this.isApplyingPendingConfiguration = false;
    } catch (e) {
      console.warn('Failed to capture initial state after applying saved configuration:', e);
    }
  }
  
  /**
   * Fallback initialization for edit mode (old behavior)
   */
  private initializeForEditFallback(goalName: string, goalDescription: string | undefined, taskTargetColumn: string[], existingTasks: TaskBase[]): void {
    // Initialize form data with existing goal
    this.formData.name = goalName;
    this.formData.description = goalDescription;
    this.formData.targetColumns = taskTargetColumn;
    
    // Lock the target columns for edit mode
    this.lockedTargetColumns = [...taskTargetColumn];
    
    // If the goal has tasks, populate the goalTasks array and use the first task's data for the form
    if (existingTasks.length > 0) {
      // Convert existing tasks to our format
      this.goalTasks = existingTasks.map(task => {
        // Extract targetColumn from task if available
        const taskColumns = 'targetColumn' in task ? (task as any).targetColumn : taskTargetColumn;
        return {
          goalType: task.taskId,
          name: this.formData.name, // Use goal name for tasks
          description: task.description || this.formData.description,
          parameters: task.parameters || (task.parametersJson ? JSON.parse(task.parametersJson) : {}),
          targetColumns: [...taskColumns]
        };
      });
      
      const firstTask = existingTasks[0];
      if (firstTask) {
        this.formData.goalType = firstTask.taskId;
        
        // Parse parameters from JSON if available
        if (firstTask.parametersJson) {
          try {
            this.formData.parameters = JSON.parse(firstTask.parametersJson);
          } catch (error) {
            console.warn('Failed to parse task parameters JSON:', error);
            this.formData.parameters = firstTask.parameters || {};
          }
        } else {
          this.formData.parameters = firstTask.parameters || {};
        }
      }
    }
  }

  toggleGoalTemplateFavorite(taskName: string, event: Event): void {
    event.stopPropagation(); // Prevent card selection
    
    const taskIndex = this.goalTemplates.findIndex(t => t.key === taskName);
    if (taskIndex === -1) return;
    
    const task = this.goalTemplates[taskIndex];
    const isFavorite = task.isFavorite ?? false;
    
    if (isFavorite) {
      // Remove from favorites
      this.loadingFavorites = true;
      this.dataService.removeFavoriteTask(taskName).subscribe({
        next: () => {
          this.favoriteGoals.delete(taskName);
          // Create new array reference to trigger change detection
          this.goalTemplates = this.goalTemplates.map((t, i) => 
            i === taskIndex ? { ...t, isFavorite: false } : t
          );
          this.loadingFavorites = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error removing favorite task:', error);
          this.loadingFavorites = false;
          // Show error message
          this.error = 'Failed to remove favorite task';
          setTimeout(() => this.error = null, 3000);
        }
      });
    } else {
      // Add to favorites
      this.loadingFavorites = true;
      this.dataService.addFavoriteTask(taskName).subscribe({
        next: () => {
          this.favoriteGoals.add(taskName);
          // Create new array reference to trigger change detection
          this.goalTemplates = this.goalTemplates.map((t, i) => 
            i === taskIndex ? { ...t, isFavorite: true } : t
          );
          this.loadingFavorites = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error adding favorite task:', error);
          this.loadingFavorites = false;
          // Show error message
          this.error = 'Failed to add favorite task';
          setTimeout(() => this.error = null, 3000);
        }
      });
    }
  }

  isGoalTemplateFavorite(taskName: string): boolean {
    const task = this.goalTemplates.find(t => t.key === taskName);
    return task?.isFavorite ?? false;
  }

  toggleShowFavorites(showFavorites: boolean): void {
    this.showFavorites = showFavorites;
  }

  toggleAdvancedOptions(): void {
    this.advancedOptionsExpanded = !this.advancedOptionsExpanded;
  }

  // Category expansion management
  setCategoryExpanded(category: string, expanded: boolean): void {
    if (expanded) {
      this.expandedCategories.add(category);
    } else {
      this.expandedCategories.delete(category);
    }
  }

  isCategoryExpanded(category: string): boolean {
    return this.expandedCategories.has(category);
  }

  // Helper methods for template
  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  formatArrayValue(value: any): string {
    return Array.isArray(value) ? value.join(', ') : value;
  }

  getArrayValue(value: any): any[] {
    return Array.isArray(value) ? value : [value];
  }

  // Parameter value management
  getParameterValue(key: string): any {
    return this.formData.parameters?.[key];
  }

  setParameterValue(key: string, value: any): void {
    if (!this.formData.parameters) {
      this.formData.parameters = {};
    }
    this.formData.parameters[key] = value;
  }

  // Convert form data to TaskCreate object
  createTaskBaseFromFormData(): any {
    const targetColumns = this.formData.targetColumns || [];
    const taskCreate = {
      taskId: this.formData.goalType,
      description: this.formData.description,
      parameters: this.formData.parameters || {},
      targetColumn: targetColumns // API expects 'targetColumn' (singular) as array
    };
    
    return taskCreate;
  }

  // Create GoalCreate object from form data
  createGoalCreateFromFormData(projectId: string, targetColumns: string[] = []): any {
    const taskCreate = this.createTaskBaseFromFormData();
    
    // Note: targetColumn is now per task, not per goal
    const goalCreate = {
      projectId: projectId,
      goalName: this.formData.name || 'Untitled Goal',
      description: this.formData.description,
      isActive: this.formData.isActive !== undefined ? this.formData.isActive : true,
      tasks: [taskCreate] // Array of tasks with targetColumn on each task
    };
    
    return goalCreate;
  }

  // Create GoalUpdate object from form data
  createGoalUpdateFromFormData(): GoalUpdate {
    const taskCreate = this.createTaskBaseFromFormData();
    
    // Note: targetColumn is now per task, not per goal
    const goalUpdate: GoalUpdate = {
      goalId: this.effectiveData.goalData?.goalId,
      goalName: this.formData.name || 'Untitled Goal',
      description: this.formData.description,
      isActive: this.formData.isActive !== undefined ? this.formData.isActive : true,
      tasks: [taskCreate] // Array of tasks with targetColumn on each task
    };
    
    return goalUpdate;
  }

  canProceed(): boolean {
    return this.formData.goalType !== '';
  }

  canCreate(): boolean {
    // Can create if at least one column is selected AND at least one task is enabled
    const hasColumns = !!(this.formData.targetColumns && this.formData.targetColumns.length > 0);
    const hasTasks = this.taskCatalogWithState.some(task => task.enabled);
    return hasColumns && hasTasks;
  }
  
  /**
   * Check if columns are selected (required before interacting with tasks)
   */
  hasColumnsSelected(): boolean {
    return !!(this.formData.targetColumns && this.formData.targetColumns.length > 0);
  }
  
  /**
   * Determine if a task is mandatory based on metadata
   */
  isTaskMandatory(task: TaskCatalogGet): boolean {
    try {
      if (task.taskMetadata) {
        const metadata = JSON.parse(task.taskMetadata);
        return metadata.mandatory === true || metadata.isMandatory === true;
      }
    } catch (e) {
      console.warn('Failed to parse task metadata:', e);
    }
    return false;
  }
  
  /**
   * Toggle task enabled state
   */
  toggleTaskEnabled(task: TaskCatalogGet & { enabled: boolean; mandatory: boolean; required: boolean }): void {
    if (task.mandatory) {
      return; // Cannot toggle mandatory tasks
    }
    if (!this.hasColumnsSelected()) {
      return; // Cannot toggle tasks without columns selected
    }
    
    const wasDisabled = !task.enabled;
    task.enabled = !task.enabled;
    
    // When enabling a task, use the user's selected columns
    if (wasDisabled && task.enabled) {
      this.taskColumnSelections.set(task.taskId, [...(this.formData.targetColumns || [])]);
    }
  }
  
  /**
   * Handle task enabled state change from slide toggle
   */
  onTaskEnabledChange(task: TaskCatalogGet & { enabled: boolean; mandatory: boolean; required: boolean }, newValue: boolean): void {
    // When transitioning from disabled to enabled, use the user's selected columns
    if (newValue) {
      this.taskColumnSelections.set(task.taskId, [...(this.formData.targetColumns || [])]);
    }
  }
  
  /**
   * Select a task to view in the right panel
   */
  selectTaskForViewing(task: TaskCatalogGet & { enabled: boolean; mandatory: boolean; required: boolean }): void {
    if (!this.hasColumnsSelected()) {
      return; // Cannot view tasks without columns selected
    }
    this.selectedTaskForView = task;
  }
  
  /**
   * Get editable parameters from task metadata
   */
  getTaskParameters(task: TaskCatalogGet & { enabled: boolean; mandatory: boolean; required: boolean }): Array<{ key: string; name?: string; value: any; default?: any; description?: string; type?: string; enum?: string[]; required?: boolean }> {
    // Check cache first to prevent re-parsing
    if (this.taskParametersCache.has(task.taskId)) {
      return this.taskParametersCache.get(task.taskId)!;
    }
    
    let parameters: Array<{ key: string; name?: string; value: any; default?: any; description?: string; type?: string; enum?: string[]; required?: boolean }> = [];
    
    try {
      if (task.taskMetadata) {
        const metadata = JSON.parse(task.taskMetadata);
        const params = metadata.params;
        
        // Check if params has properties (new schema format)
        if (params?.properties) {
          // Convert properties object to array of parameter objects
          parameters = Object.keys(params.properties).map(key => {
            const prop = params.properties[key];
            const isRequired = params.required?.includes(key) || (prop.default === undefined && !prop.anyOf);

            // Extract enum from either the property itself or from items.enum for array types
            let enumValues = prop.enum;
            if (prop.type === 'array' && prop.items?.enum) {
              enumValues = prop.items.enum;
            }

            // Detect column type hints in various metadata conventions
            let detectedType: any = prop.type;
            let itemsType: string | null = null;
            try {
              if (prop.ui_type === 'column' || prop.input_type === 'column' || prop.inputType === 'column' || prop['x-input-type'] === 'column' || prop.format === 'column' || prop['x-format'] === 'column' || prop.type === 'column') {
                detectedType = 'column';
              }
            } catch (e) {
              // ignore
            }

            if (prop.type === 'array' && prop.items) {
              itemsType = prop.items.type || null;
              if (prop.items.input_type === 'column' || prop.items.inputType === 'column' || prop.items['x-input-type'] === 'column' || prop.items.format === 'column') {
                itemsType = 'column';
              }
            }

            const paramObj: any = {
              key: key,
              name: prop.title || key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
              value: prop.default !== undefined ? prop.default : null,
              default: prop.default,
              description: prop.description,
              type: detectedType,
              enum: enumValues,
              required: isRequired
            };

            if (itemsType) {
              paramObj.itemsType = itemsType;
            }

            return paramObj;
          });
          
          // Sort parameters: required first, then optional
          parameters.sort((a, b) => {
            if (a.required && !b.required) return -1;
            if (!a.required && b.required) return 1;
            return 0;
          });
        }
        // Fallback to old format (flat params object)
        else if (params && typeof params === 'object') {
          parameters = Object.keys(params).map(key => ({
            key: key,
            name: key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
            value: params[key],
            default: params[key],
            description: undefined,
            required: false
          }));
        }
      }
    } catch (e) {
      console.warn('Failed to parse task metadata for parameters:', e);
    }
    
    // Apply suggested parameters if available
    const suggestedParam = this.suggestedParams.get(task.taskId);
    if (suggestedParam) {
      parameters.forEach(param => {
        // Check if this parameter has a suggested value
        if (suggestedParam.hasOwnProperty(param.key)) {
          const suggestedValue = suggestedParam[param.key];
          // Only apply if the current value is null/undefined or still at default
          if (param.value === null || param.value === undefined || param.value === param.default) {
            param.value = suggestedValue;
            console.log(`Applied suggested value for ${task.taskId}.${param.key}:`, suggestedValue);
          }
        }
      });
    }
    
    // Cache the result
    this.taskParametersCache.set(task.taskId, parameters);
    return parameters;
  }
  
  /**
   * Check if a parameter is required
   */
  isParameterRequired(param: { required?: boolean }): boolean {
    return param.required === true;
  }
  
  /**
   * Handle parameter change for a task
   */
  onParameterChange(task: TaskCatalogGet & { enabled: boolean; mandatory: boolean; required: boolean }): void {
    // Auto-enable task if user is configuring it while disabled
    this.autoEnableTaskIfDisabled(task);
  }
  
  /**
   * Auto-enable a task if it's currently disabled (but not mandatory)
   */
  private autoEnableTaskIfDisabled(task: TaskCatalogGet & { enabled: boolean; mandatory: boolean; required: boolean }): void {
    if (!task.enabled && !task.mandatory) {
      task.enabled = true;
      // When enabling a task, select all available columns
      const allColumns = this.effectiveData.schemaData?.columns?.map(col => col.name) || [];
      this.taskColumnSelections.set(task.taskId, [...allColumns]);
    }
  }
  
  /**
   * Toggle column selection expanded state
   */
  toggleColumnSelection(): void {
    // Auto-enable task if user is configuring it while disabled
    if (this.selectedTaskForView) {
      this.autoEnableTaskIfDisabled(this.selectedTaskForView);
    }
    this.columnSelectionExpanded = !this.columnSelectionExpanded;
  }
  
  /**
   * Check if a column is selected for a specific task
   */
  isColumnSelectedForTask(task: TaskCatalogGet & { enabled: boolean; mandatory: boolean; required: boolean }, column: string): boolean {
    const selectedColumns = this.taskColumnSelections.get(task.taskId);
    return selectedColumns ? selectedColumns.includes(column) : false;
  }
  
  /**
   * Toggle column selection for a specific task
   */
  toggleColumnForTask(task: TaskCatalogGet & { enabled: boolean; mandatory: boolean; required: boolean }, column: string, selected: boolean): void {
    if (task.mandatory) {
      return; // Cannot change columns for mandatory tasks
    }
    
    // Auto-enable task if user is configuring it while disabled
    this.autoEnableTaskIfDisabled(task);
    
    let selectedColumns = this.taskColumnSelections.get(task.taskId) || [];
    
    if (selected) {
      // Add column if not already present
      if (!selectedColumns.includes(column)) {
        selectedColumns = [...selectedColumns, column];
      }
    } else {
      // Remove column
      selectedColumns = selectedColumns.filter(col => col !== column);
    }
    
    this.taskColumnSelections.set(task.taskId, selectedColumns);
    
    // If all columns are deselected, disable the task
    if (selectedColumns.length === 0 && !task.mandatory) {
      task.enabled = false;
    }
  }

  // Enable Add button only if form is valid for a task
  canAddTask(): boolean {
    return this.formData.goalType !== '' &&
           !!this.formData.targetColumns &&
           this.formData.targetColumns.length > 0 &&
           this.areRequiredParametersFilled();
  }

  // Check if all required parameters are filled
  areRequiredParametersFilled(): boolean {
    const requiredFields = this.getMetadataFieldsForConfiguration();
    if (!requiredFields.length) return true;
    return requiredFields.every(field => {
      const value = this.getParameterValue(field.key);
      return value !== null && value !== undefined && value !== '';
    });
  }

  // Check if columns are locked (tasks have been added)
  areColumnsLocked(): boolean {
    // Allow users to select different columns for each task
    return false;
  }

  // Get the effective target columns (locked columns if available, otherwise form data)
  getEffectiveTargetColumns(): string[] {
    return this.lockedTargetColumns.length > 0 ? this.lockedTargetColumns : (this.formData.targetColumns || []);
  }

  // Add current form as a new task
  onAddTask(): void {
    if (!this.canAddTask()) return;
    
    // Get default values from advanced fields
    const defaultParameters = { ...this.formData.parameters };
    const advancedFields = this.getAdvancedMetadataFields();
    advancedFields.forEach(field => {
      if (field.defaultValue !== null && field.defaultValue !== undefined && field.defaultValue !== '') {
        defaultParameters[field.key] = field.defaultValue;
      }
    });
    
    // Clone current formData for the task with all parameters including defaults
    const newTask = {
      goalType: this.formData.goalType,
      name: this.formData.name,
      description: this.formData.description,
      parameters: defaultParameters,
      targetColumns: [...(this.formData.targetColumns || [])]
    };
    this.goalTasks.push(newTask);
    
    // No longer lock columns - allow different column selections for each task
    
    this.resetFormForNewTask();
  }

  // Remove a task from the list
  removeTask(index: number): void {
    if (index >= 0 && index < this.goalTasks.length) {
      this.goalTasks.splice(index, 1);
      
      // No longer need to unlock columns since they're never locked
    }
  }

  // Reset form for new task entry
  resetFormForNewTask(): void {
    // Keep the selected goalType, but clear other fields
    this.formData.name = '';
    this.formData.description = '';
    this.formData.parameters = {};
    
    // Clear columns to allow selection of different columns for the next task
    this.formData.targetColumns = [];
    
    this.clearFieldCache();
    this.hasAttemptedCreate = false;
    // Optionally, keep goalType or reset it as needed
  }

  shouldShowColumnValidationError(): boolean {
    return this.hasAttemptedCreate && 
           (!this.formData.targetColumns || this.formData.targetColumns.length === 0);
  }

  onColumnsChange(): void {
    // Reset the attempted create flag when columns are changed
    if (this.formData.targetColumns && this.formData.targetColumns.length > 0) {
      this.hasAttemptedCreate = false;
    }
    
    // Apply newly selected columns to all active/enabled tasks
    this.taskCatalogWithState.forEach(task => {
      if (task.enabled) {
        // Set the goal-level columns for all enabled tasks
        this.taskColumnSelections.set(task.taskId, [...(this.formData.targetColumns || [])]);
        console.log(`Applied columns to enabled task ${task.taskKey}:`, this.formData.targetColumns);
      }
    });
  }

  // ============================================================================
  // GOAL-LEVEL COLUMN CHIP SELECTOR METHODS
  // ============================================================================
  
  /**
   * Get sorted columns with data types for chip selector display
   * Suggested columns from the suggest-columns endpoint appear first,
   * followed by remaining columns sorted alphabetically
   */
  get sortedGoalColumns(): { name: string; dataType: string }[] {
    if (!this.effectiveData.schemaData?.columns) {
      return [];
    }
    
    // If we have suggested columns, prioritize them
    if (this.suggestedColumns.length > 0) {
      const suggested = this.effectiveData.schemaData.columns
        .filter(col => this.suggestedColumns.includes(col.name));
      
      const remaining = this.effectiveData.schemaData.columns
        .filter(col => !this.suggestedColumns.includes(col.name))
        .sort((a, b) => a.name.localeCompare(b.name));
      
      return [...suggested, ...remaining];
    }
    
    // Return columns sorted alphabetically if no suggestions
    return this.effectiveData.schemaData.columns
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  
  /**
   * Get only the suggested columns
   */
  get suggestedGoalColumns(): { name: string; dataType: string }[] {
    if (!this.effectiveData.schemaData?.columns || this.suggestedColumns.length === 0) {
      return [];
    }
    
    return this.effectiveData.schemaData.columns
      .filter(col => this.suggestedColumns.includes(col.name));
  }
  
  /**
   * Get only the remaining (non-suggested) columns, sorted alphabetically
   */
  get remainingGoalColumns(): { name: string; dataType: string }[] {
    if (!this.effectiveData.schemaData?.columns) {
      return [];
    }
    
    // If no suggestions, return all columns sorted
    if (this.suggestedColumns.length === 0) {
      return this.effectiveData.schemaData.columns
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    
    // Return only non-suggested columns, sorted
    return this.effectiveData.schemaData.columns
      .filter(col => !this.suggestedColumns.includes(col.name))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  
  /**
   * Check if a column is selected at the goal level
   */
  isGoalColumnSelected(columnName: string): boolean {
    return !!(this.formData.targetColumns && this.formData.targetColumns.includes(columnName));
  }
  
  /**
   * Check if a column is suggested by the suggest-columns endpoint
   */
  isColumnSuggested(columnName: string): boolean {
    return this.suggestedColumns.includes(columnName);
  }
  
  /**
   * Toggle column selection at the goal level
   */
  toggleGoalColumnSelection(columnName: string): void {
    if (!this.formData.targetColumns) {
      this.formData.targetColumns = [];
    }
    
    const index = this.formData.targetColumns.indexOf(columnName);
    if (index > -1) {
      // Remove column
      this.formData.targetColumns.splice(index, 1);
    } else {
      // Add column
      this.formData.targetColumns.push(columnName);
    }
    
    // Trigger the existing onColumnsChange logic
    this.onColumnsChange();
  }
  
  /**
   * Select all columns at the goal level
   */
  selectAllGoalColumns(): void {
    if (!this.effectiveData.schemaData?.columns) {
      return;
    }
    
    this.formData.targetColumns = this.effectiveData.schemaData.columns.map(col => col.name);
    
    // Trigger the existing onColumnsChange logic
    this.onColumnsChange();
  }
  
  /**
   * Deselect all columns at the goal level
   */
  deselectAllGoalColumns(): void {
    this.formData.targetColumns = [];
    
    // Trigger the existing onColumnsChange logic
    this.onColumnsChange();
  }

  // ============================================================================
  // END GOAL-LEVEL COLUMN CHIP SELECTOR METHODS
  // ============================================================================

  onNext(): void {
    if (this.debugChangeDetection) console.log('onNext called', { currentStage: this.currentStage, canProceed: this.canProceed(), goalType: this.formData.goalType });
    if (this.currentStage === 'goal-selection' && this.canProceed()) {
      // Find the selected task
      const selectedTask = this.goalTemplates.find(task => task.key === this.formData.goalType);
      
      // Set default goal name if not already set
      if (!this.formData.name || !this.formData.name.trim()) {
        this.formData.name = selectedTask?.metadata.name || `${this.formatCategoryTitle(this.formData.goalType)} Goal`;
      }
      // Set default description if not already set
      if (!this.formData.description || !this.formData.description.trim()) {
        this.formData.description = selectedTask?.metadata.description || 'Goal description';
      }
      
      // Call suggest-columns endpoint if we have schema data
      // Wait for suggestions to complete before proceeding to ensure columns are set
      if (this.effectiveData.schemaData?.columns && this.formData.goalType) {
        this.loading = true;
        this.fetchColumnSuggestions(true, () => {
          // Callback after suggestions are loaded
          this.loading = false;
          this.proceedToConfiguration();
        });
      } else {
        // No suggestions to fetch, proceed immediately
        this.proceedToConfiguration();
      }
    }
  }
  
  private proceedToConfiguration(): void {
    this.currentStage = 'goal-configuration';
    
    // Capture initial state for change tracking
    this.captureInitialState();
    
    this.emitTitle();
  }

  onBack(): void {
    if (this.currentStage === 'goal-configuration') {
      // Check if there are unsaved changes or if editing an existing goal
      if (this.hasUnsavedChanges() || this.isEditMode) {
        const dialogRef = this.dialog.open(SaveConfirmDialogComponent, {
          width: '450px',
          data: {
            title: 'Unsaved Changes',
            message: this.isEditMode 
              ? 'You have unsaved changes to this goal. Would you like to save them before going back?'
              : 'You have unsaved changes. Would you like to save them before going back?',
            saveText: 'Save',
            discardText: 'Discard',
            cancelText: 'Cancel'
          } as SaveConfirmDialogData
        });
        
        dialogRef.afterClosed().subscribe((result: SaveConfirmDialogResult) => {
          if (result === 'save') {
            // User wants to save, call onConfirm which handles save and close
            this.onConfirm();
          } else if (result === 'discard') {
            // User confirmed discard, go back
            this.currentStage = 'goal-selection';
            this.emitTitle();
          }
          // If 'cancel' or undefined, do nothing (stay on current stage)
        });
      } else {
        // No changes, go back directly
        this.currentStage = 'goal-selection';
        this.emitTitle();
      }
    }
  }
  
  /**
   * Fetch column suggestions from the suggest-columns endpoint
   * @param autoSelect - If true, automatically select suggested columns (for new goals). If false, only store suggestions for display (for existing goals).
   * @param onComplete - Optional callback to execute when the API call completes (success or failure)
   */
  private fetchColumnSuggestions(autoSelect: boolean = false, onComplete?: () => void): void {
    if (!this.formData.goalType) {
      if (onComplete) onComplete();
      return;
    }
    
    // Get the full schema from the dialog data
    const fullSchema = this.effectiveData.fullSchema;
    
    if (!fullSchema || fullSchema.length === 0) {
      console.warn('No full schema available for suggest-columns');
      if (onComplete) onComplete();
      return;
    }
    
    // Prepare the request with full schema structure
    const request = {
      goals: [this.formData.goalType],
      schema: fullSchema
    };
    
    // Call the suggest-columns endpoint
    this.dataService.suggestColumns(request).subscribe({
      next: (response) => {
        try {
          // Handle case where response might be a string that needs parsing
          let parsedResponse: any = response;
          if (typeof response === 'string') {
            parsedResponse = JSON.parse(response);
          }
          
          // Check if response has results property
          if (!parsedResponse || !parsedResponse.results) {
            console.error('Invalid response structure - no results property');
            if (onComplete) onComplete();
            return;
          }
          
          // Find the result for the selected goal
          const result = parsedResponse.results.find((r: any) => r.goal_id === this.formData.goalType);
          
          if (result && result.matched) {
          // Store suggested columns
          this.suggestedColumns = result.target_columns || [];
          
          // Store suggested parameters by task_id
          this.suggestedParams.clear();
          if (result.params && result.params.length > 0) {
            result.params.forEach((param: any) => {
              this.suggestedParams.set(param.task_id, param);
            });
          }
          
          // Auto-select suggested columns if requested (new goals only)
          if (autoSelect && this.suggestedColumns.length > 0) {
            // For new goals, auto-select the suggested columns
            if (!this.formData.targetColumns) {
              this.formData.targetColumns = [];
            }
            // Add suggested columns that aren't already selected
            this.suggestedColumns.forEach(col => {
              if (!this.formData.targetColumns!.includes(col)) {
                this.formData.targetColumns!.push(col);
              }
            });
            
            console.log('Auto-selected columns:', this.formData.targetColumns);
            
            // CRITICAL: Trigger onColumnsChange to apply columns to enabled tasks
            // This ensures columns are set in taskColumnSelections for all enabled tasks
            this.onColumnsChange();
            
            // Trigger change detection to ensure UI updates
            this.cdr.detectChanges();
          }
          
          // Clear parameter cache so suggested values will be applied
          this.taskParametersCache.clear();
        } else {
          console.log('No matched result found for goal:', this.formData.goalType);
        }
      } catch (err) {
        console.error('Exception processing suggest-columns response:', err);
      }
      
      // Call completion callback
      if (onComplete) onComplete();
      },
      error: (error) => {
        console.warn('Failed to fetch column suggestions:', error);
        // Continue without suggestions - not a critical failure
        
        // Call completion callback even on error
        if (onComplete) onComplete();
      }
    });
  }
  
  /**
   * Capture initial state when entering step 2
   */
  private captureInitialState(): void {
    if (this.debugChangeDetection) console.log('captureInitialState: start', { taskCount: this.taskCatalogWithState.length, targetColumns: this.formData.targetColumns });
    this.initialTaskStates = JSON.stringify(
      this.taskCatalogWithState.map(t => ({ id: t.taskId, enabled: t.enabled }))
    );
    this.initialColumns = [...(this.formData.targetColumns || [])];

    // Capture initial parameter values for all tasks
    this.initialParameterValues.clear();
    for (const task of this.taskCatalogWithState) {
      const params = this.getTaskParameters(task);
      const paramValues = new Map<string, any>();
      for (const param of params) {
        try {
          paramValues.set(param.key, this.deepClone(param.value));
        } catch (e) {
          if (this.debugChangeDetection) console.warn('captureInitialState: failed to clone param value', { taskId: task.taskId, key: param.key, err: e });
          paramValues.set(param.key, param.value);
        }
      }
      this.initialParameterValues.set(task.taskId, paramValues);
    }

    // Also capture the full save payload snapshot for robust comparison
    try {
      const payload = this.buildSavePayload();
      this.initialSavePayload = this.stableStringify(payload);
      if (this.debugChangeDetection) console.log('captureInitialState: initialSavePayload set', { length: (this.initialSavePayload || '').length });
    } catch (e) {
      this.initialSavePayload = null;
      if (this.debugChangeDetection) console.warn('captureInitialState: failed to build initial save payload', e);
    }
    if (this.debugChangeDetection) console.log('captureInitialState: done', { initialTaskStates: this.initialTaskStates, initialColumns: this.initialColumns });
  }
  
  /**
   * Check if there are unsaved changes
   */
  hasUnsavedChanges(): boolean {
    // Entry log to verify change-detection is invoked (guarded)
    try {
      if (this.debugChangeDetection) console.log('ChangeDetection: hasUnsavedChanges called', {
        currentStage: this.currentStage,
        isEditMode: this.isEditMode,
        initialSavePayloadPresent: this.initialSavePayload != null
      });
    } catch (e) {
      // ignore logging errors
    }
    // If we are actively applying a pending saved configuration, suppress
    // unsaved-change prompts until the snapshot has been captured.
    if (this.isApplyingPendingConfiguration) {
      if (this.debugChangeDetection) console.log('ChangeDetection: applying pending configuration — treating as no unsaved changes');
      return false;
    }

    // If we are in edit mode but haven't yet captured the initial payload,
    // fall back to field-level checks below rather than suppressing the dialog.
    if (this.isEditMode && this.initialSavePayload == null) {
      if (this.debugChangeDetection) console.log('ChangeDetection: initialSavePayload not set — falling back to field-level checks');
      // continue to fallback checks
    }

    // If we have an initial save payload, compare the canonical payloads
    try {
      const currentPayload = this.stableStringify(this.buildSavePayload());
      if (this.initialSavePayload != null) {
        // Debug logging to help diagnose false positives in change detection
        try {
          const equal = currentPayload === this.initialSavePayload;
          if (!equal) {
            // Log structured difference to help diagnose the cause
            try {
              const initialObj = JSON.parse(this.initialSavePayload!);
              const currentObj = JSON.parse(currentPayload);
              console.warn('ChangeDetection: payloads differ', { initial: initialObj, current: currentObj });
            } catch (parseErr) {
              console.warn('ChangeDetection: payloads differ (could not parse)', { initial: this.initialSavePayload, current: currentPayload, parseErr });
            }
          } else if (this.debugChangeDetection) {
            console.log('ChangeDetection: payloadsEqual=true');
          }
          return !equal;
        } catch (logErr) {
          if (this.debugChangeDetection) console.warn('ChangeDetection: failed to log payloads', logErr);
          return currentPayload !== this.initialSavePayload;
        }
      }
      // Fallback: if we don't have an initial payload, fall back to field-level checks
    } catch (e) {
      // If building payload fails for some reason, fall back to existing checks below
      console.warn('Failed to build/compare save payload for change detection:', e);
    }

    // --- Fallback checks (maintained for backward compatibility) ---
    // Check if task enabled states have changed
    const currentTaskStates = JSON.stringify(
      this.taskCatalogWithState.map(t => ({ id: t.taskId, enabled: t.enabled }))
    );
    if (currentTaskStates !== this.initialTaskStates) {
      return true;
    }

    // Check if columns have changed
    const currentColumns = this.formData.targetColumns || [];
    if (currentColumns.length !== this.initialColumns.length) {
      return true;
    }
    if (!currentColumns.every((col, idx) => col === this.initialColumns[idx])) {
      return true;
    }

    // Check if any task parameters have been modified from their initial values
    for (const task of this.taskCatalogWithState) {
      const params = this.getTaskParameters(task);
      const initialValues = this.initialParameterValues.get(task.taskId);

      if (!initialValues) {
        // If we don't have initial values, compare against defaults (backward compatibility)
        for (const param of params) {
          if (this.stableStringify(param.value) !== this.stableStringify(param.default)) {
            return true;
          }
        }
      } else {
        // Compare current values against initial values
        for (const param of params) {
          const initialValue = initialValues.get(param.key);
          if (this.stableStringify(param.value) !== this.stableStringify(initialValue)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * Build the canonical save payload for the goal based on current form state.
   * This should match the server payload structure used when saving.
   */
  private buildSavePayload(): any {
    const tasks = this.taskCatalogWithState.map(t => {
      const params = this.getTaskParameters(t).reduce((acc: any, p: any) => {
        acc[p.key] = this.deepClone(p.value);
        return acc;
      }, {});

      return {
        taskId: t.taskId,
        enabled: !!t.enabled,
        parameters: params,
        columns: this.taskColumnSelections.get(t.taskId) ? [...this.taskColumnSelections.get(t.taskId)!] : []
      };
    });

    // Sort tasks by taskId to ensure deterministic ordering in payload
    tasks.sort((a: any, b: any) => (a.taskId || '').localeCompare(b.taskId || ''));

    const payload = {
      goalType: this.formData.goalType,
      name: this.formData.name || '',
      description: this.formData.description || '',
      isActive: !!this.formData.isActive,
      targetColumns: Array.isArray(this.formData.targetColumns) ? [...this.formData.targetColumns].sort() : [],
      tasks: tasks
    };

    return payload;
  }

  // Get the selected task
  getSelectedGoalTemplate(): GoalTemplateItem | undefined {
    if (!this.formData.goalType) {
      return undefined;
    }
    
    const task = this.goalTemplates.find(task => task.key === this.formData.goalType);
    return task;
  }

  // Get unique metadata structure for the selected task
  getUniqueMetadataForSelectedTask(): { [key: string]: Set<any> } {
    const selectedTask = this.getSelectedGoalTemplate();
    if (!selectedTask) return {};

    const metadataMap: { [key: string]: Set<any> } = {};
    const metadata = selectedTask.metadata;
    
    // Extract basic properties
    metadataMap['input_type'] = new Set([metadata.input_type]);
    metadataMap['output_type'] = new Set([metadata.output_type]);

    // Extract parameter keys and types
    if (metadata.parameters) {
      Object.keys(metadata.parameters).forEach(paramKey => {
        const paramMetaKey = `parameter_${paramKey}`;
        metadataMap[paramMetaKey] = new Set();
        
        const param = metadata.parameters![paramKey];
        metadataMap[paramMetaKey].add({
          input_type: param.input_type,
          description: param.description,
          default: param.default,
          allowed: param.allowed
        });
      });
    }

    // Extract input/output structure if they're objects
    if (typeof metadata.input === 'object' && metadata.input !== null) {
      Object.keys(metadata.input as Record<string, string>).forEach(inputKey => {
        const inputMetaKey = `input_${inputKey}`;
        metadataMap[inputMetaKey] = new Set();
        metadataMap[inputMetaKey].add((metadata.input as Record<string, string>)[inputKey]);
      });
    }

    if (typeof metadata.output === 'object' && metadata.output !== null) {
      Object.keys(metadata.output as Record<string, string>).forEach(outputKey => {
        const outputMetaKey = `output_${outputKey}`;
        metadataMap[outputMetaKey] = new Set();
        metadataMap[outputMetaKey].add((metadata.output as Record<string, string>)[outputKey]);
      });
    }

    return metadataMap;
  }

  // Get a simplified view of unique metadata entries
  getMetadataEntries(): { key: string; values: any[]; type: string }[] {
    const metadataMap = this.getUniqueMetadataForSelectedTask();
    const entries: { key: string; values: any[]; type: string }[] = [];

    Object.keys(metadataMap).forEach(key => {
      const values = Array.from(metadataMap[key]);
      let type = 'string';
      
      if (key.startsWith('parameter_')) {
        type = 'parameter';
      } else if (key.startsWith('input_')) {
        type = 'input';
      } else if (key.startsWith('output_')) {
        type = 'output';
      } else if (key.includes('_type')) {
        type = 'type';
      }

      entries.push({
        key: key.replace(/^(parameter_|input_|output_)/, ''),
        values,
        type
      });
    });

    return entries.sort((a, b) => a.key.localeCompare(b.key));
  }

  // Get metadata fields that need configuration (no default value) - CACHED
  getMetadataFieldsForConfiguration(): { key: string; label: string; fieldType: string; description?: string; placeholder?: string; options?: any[]; min?: number; max?: number; step?: number }[] {
    const selectedTask = this.getSelectedGoalTemplate();
    if (!selectedTask) return [];

    // Check cache validity
    if (this._cachedRequiredFields && this._lastSelectedTaskName === selectedTask.key) {
      return this._cachedRequiredFields;
    }

    const configFields: any[] = [];
    const metadata = selectedTask.metadata;

    // Process parameters that don't have default values
    if (metadata.parameters) {
      Object.keys(metadata.parameters).forEach(paramKey => {
        const param = metadata.parameters![paramKey];
        
        // Only include parameters without default values or with null/undefined defaults
        if (param.default === null || param.default === undefined || param.default === '') {
          configFields.push(this.createFieldConfig(paramKey, param));
        }
      });
    }

    // Cache the results
    this._cachedRequiredFields = configFields;
    this._lastSelectedTaskName = selectedTask.key;

    return configFields;
  }
  
  // Check if there are any required parameters to show
  hasRequiredParameters(): boolean {
    return this.getMetadataFieldsForConfiguration().length > 0;
  }

  // Get metadata fields that have default values (for advanced options) - CACHED
  getAdvancedMetadataFields(): { key: string; label: string; fieldType: string; description?: string; placeholder?: string; options?: any[]; min?: number; max?: number; step?: number; defaultValue?: any }[] {
    const selectedTask = this.getSelectedGoalTemplate();
    if (!selectedTask) return [];

    // Check cache validity
    if (this._cachedAdvancedFields && this._lastSelectedTaskName === selectedTask.key) {
      return this._cachedAdvancedFields;
    }

    const advancedFields: any[] = [];
    const metadata = selectedTask.metadata;

    // Process parameters that have default values
    if (metadata.parameters) {
      Object.keys(metadata.parameters).forEach(paramKey => {
        const param = metadata.parameters![paramKey];
        
        // Only include parameters with default values
        if (param.default !== null && param.default !== undefined && param.default !== '') {
          const fieldConfig = this.createFieldConfig(paramKey, param);
          fieldConfig.defaultValue = param.default;
          advancedFields.push(fieldConfig);
        }
      });
    }

    // Cache the results
    this._cachedAdvancedFields = advancedFields;
    this._lastSelectedTaskName = selectedTask.key;

    return advancedFields;
  }

  // Helper method to create field configuration
  private createFieldConfig(paramKey: string, param: any): any {
    const fieldConfig: any = {
      key: paramKey,
      label: this.formatFieldLabel(paramKey),
      fieldType: this.mapInputTypeToFieldType(param.input_type),
      description: param.description,
      placeholder: `Enter ${this.formatFieldLabel(paramKey).toLowerCase()}`
    };

    // Add options for select fields
    if (param.input_type === 'select' || param.input_type === 'select-multiple') {
      fieldConfig.options = param.allowed || [];
    }

    // Add range configuration for numeric fields
    if (param.input_type === 'range' && Array.isArray(param.allowed) && param.allowed.length === 2) {
      fieldConfig.min = param.allowed[0];
      fieldConfig.max = param.allowed[1];
      fieldConfig.step = 1;
    }

    return fieldConfig;
  }

  // Helper method to format field labels
  private formatFieldLabel(key: string): string {
    return key.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  // Helper method to map input types to field types
  private mapInputTypeToFieldType(inputType: string): string {
    const typeMap: { [key: string]: string } = {
      'text': 'text',
      'column': 'column',
      'numeric': 'number',
      'select': 'select',
      'select-multiple': 'select-multiple',
      'bool': 'bool',
      'range': 'range',
      'percentage': 'percentage',
      'dictionary': 'text',
      'List[str]': 'text'
    };
    
    return typeMap[inputType] || 'text';
  }

  // Methods for example data table display
  hasExampleData(): boolean {
    const selectedTask = this.getSelectedGoalTemplate();
    const hasData = !!(selectedTask?.metadata?.example && 
             (selectedTask.metadata.example['Input Data'] || 
              selectedTask.metadata.example['Output Data'] ||
              selectedTask.metadata.example.Comments));
    
    return hasData;
  }

  getCombinedExampleData(): Array<{input: string, output: string, comment: string}> {
    const task = this.getSelectedGoalTemplate();
    if (!task?.metadata?.example) {
      return [];
    }

    const inputData = task.metadata.example['Input Data'] || [];
    const outputData = task.metadata.example['Output Data'] || [];
    const comments = task.metadata.example.Comments || [];

    // Determine the maximum number of rows
    const maxRows = Math.max(inputData.length, outputData.length, comments.length);
    
    const combinedData: Array<{input: string, output: string, comment: string}> = [];

    for (let i = 0; i < maxRows; i++) {
      combinedData.push({
        input: this.formatRowData(inputData[i]) || '',
        output: this.formatRowData(outputData[i]) || '',
        comment: comments[i] || ''
      });
    }

    return combinedData;
  }

  private formatRowData(rowData: any): string {
    if (!rowData) return '';
    
    if (typeof rowData === 'string') {
      return rowData;
    }
    
    if (Array.isArray(rowData)) {
      return rowData.join(', ');
    }
    
    if (typeof rowData === 'object') {
      return Object.values(rowData).join(', ');
    }
    
    return String(rowData);
  }

  getTableHeaders(dataType: 'input' | 'output'): string[] {
    const selectedTask = this.getSelectedGoalTemplate();
    if (!selectedTask?.metadata?.example) return [];

    const dataKey = dataType === 'input' ? 'Input Data' : 'Output Data';
    const data = selectedTask.metadata.example[dataKey];
    
    if (!Array.isArray(data) || data.length === 0) return [];

    // Get headers from the first row (assuming it's an object)
    const firstRow = data[0];
    if (typeof firstRow === 'object' && firstRow !== null) {
      return Object.keys(firstRow);
    }

    // If it's not an object, create generic column headers
    if (Array.isArray(firstRow)) {
      return firstRow.map((_, index) => `Column ${index + 1}`);
    }

    return ['Value'];
  }

  getTableRows(dataType: 'input' | 'output'): any[][] {
    const selectedTask = this.getSelectedGoalTemplate();
    if (!selectedTask?.metadata?.example) return [];

    const dataKey = dataType === 'input' ? 'Input Data' : 'Output Data';
    const data = selectedTask.metadata.example[dataKey];
    
    if (!Array.isArray(data)) return [];

    return data.map(row => {
      if (typeof row === 'object' && row !== null && !Array.isArray(row)) {
        // Convert object to array of values
        return Object.values(row);
      } else if (Array.isArray(row)) {
        // Already an array
        return row;
      } else {
        // Single value
        return [row];
      }
    });
  }

  getCategoryIcon(category: string): string {
    // Map categories to Material icons
    const iconMap: { [key: string]: string } = {
      'Text Cleaning': 'cleaning_services',
      'Text Formatting': 'text_format',
      'analyze-train': 'analytics',
      'track-data': 'monitor',
      'data-preparation': 'data_usage',
      'visualization': 'bar_chart',
      'reporting': 'assessment'
    };
    
    return iconMap[category] || 'category';
  }

  getGoalTemplateIcon(task: GoalTemplateItem): string {
    // Use category icon for individual tasks
    return this.getCategoryIcon(task.metadata.category);
  }

  getGoalTemplateCountForCategory(category: string): number {
    return this.goalTemplates.filter(task => task.metadata.category === category).length;
  }

  formatCategoryTitle(category: string): string {
    return category.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  getCategoryDescription(category: string): string {    
    return `Goals related to ${this.formatCategoryTitle(category)}.`;
  }

  /**
   * Generate a unique goal name by checking against existing goal names
   * If a duplicate is found, append a number (e.g., "Goal Name 2", "Goal Name 3")
   * @param baseName The base name to check
   * @returns A unique goal name
   */
  generateUniqueGoalName(baseName: string): string {
    const existingNames = this.effectiveData.existingGoalNames || [];
    
    // If we're in edit mode and the name hasn't changed, keep it
    if (this.isEditMode && this.effectiveData.goalData?.goalName === baseName) {
      return baseName;
    }
    
    // If the base name doesn't exist, use it as is
    if (!existingNames.some(name => name.toLowerCase() === baseName.toLowerCase())) {
      return baseName;
    }
    
    // Find the next available number suffix
    let counter = 2;
    let candidateName = `${baseName} ${counter}`;
    
    while (existingNames.some(name => name.toLowerCase() === candidateName.toLowerCase())) {
      counter++;
      candidateName = `${baseName} ${counter}`;
    }
    
    return candidateName;
  }

  /**
   * Get the appropriate description for a task based on the current view mode
   * @param task The task to get the description for
   * @returns The simple or advanced description based on view mode
   */
  getGoalTemplateDescription(task: GoalTemplateItem): string {
    const metadata = task.metadata;
    
    // If in advanced mode and advancedDescription is available, use it
    if (this.layoutService.isAdvancedMode() && metadata.advancedDescription) {
      return metadata.advancedDescription;
    }
    
    // If in simple mode and simpleDescription is available, use it
    if (this.layoutService.isSimpleMode() && metadata.simpleDescription) {
      return metadata.simpleDescription;
    }
    
    // Fallback to regular description
    return metadata.description;
  }

  onCancel(): void {
    // Check if we're in the configuration stage and have unsaved changes
    if (this.currentStage === 'goal-configuration' && this.hasUnsavedChanges()) {
      const dialogRef = this.dialog.open(SaveConfirmDialogComponent, {
        width: '450px',
        data: {
          title: 'Unsaved Changes',
          message: this.isEditMode 
            ? 'You have unsaved changes to this goal. Would you like to save them before closing?'
            : 'You have unsaved changes. Would you like to save them before closing?',
          saveText: 'Save',
          discardText: 'Discard',
          cancelText: 'Cancel'
        } as SaveConfirmDialogData
      });
      
      dialogRef.afterClosed().subscribe((result: SaveConfirmDialogResult) => {
        if (result === 'save') {
          // User wants to save, call onConfirm which handles save and close
          this.onConfirm();
        } else if (result === 'discard') {
          // User confirmed discard, close the dialog
          if (this.isInlineMode) {
            this.inlineCancel.emit();
          } else {
            this.dialogRef.close();
          }
        }
        // If 'cancel' or undefined, do nothing (stay on dialog)
      });
    } else {
      // No unsaved changes or not in configuration stage, close directly
      if (this.isInlineMode) {
        this.inlineCancel.emit();
      } else {
        this.dialogRef.close();
      }
    }
  }

  onConfirm(): void {
    // Set the attempted create flag
    this.hasAttemptedCreate = true;
    
    // Validate: must have at least one column selected and at least one task enabled
    if (!this.canCreate()) {
      const hasColumns = !!(this.formData.targetColumns && this.formData.targetColumns.length > 0);
      const hasTasks = this.taskCatalogWithState.some(task => task.enabled);
      
      if (!hasColumns && !hasTasks) {
        this.error = 'Please select at least one column and enable at least one task before creating the goal.';
      } else if (!hasColumns) {
        this.error = 'Please select at least one column before creating the goal.';
      } else {
        this.error = 'Please enable at least one task before creating the goal.';
      }
      return;
    }

    this.loading = true;

    // Build tasks array from enabled tasks in catalog
    // Match API structure: { task_id, params, select: { columns } }
    const tasksToSave = this.taskCatalogWithState
      .filter(task => task.enabled)
      .map(task => {
        // Get configured parameters from cache (these are bound via ngModel)
        const cachedParams = this.taskParametersCache.get(task.taskId) || [];
        const params: any = {};
        
        // Convert parameter array back to object with actual configured values
        cachedParams.forEach(param => {
          if (param.value !== null && param.value !== undefined) {
            params[param.key] = param.value;
          }
        });
        
        // Get task-specific column selection, fallback to goal columns
        const taskColumns = this.taskColumnSelections.get(task.taskId) || this.formData.targetColumns || [];
        
        console.log(`Task ${task.taskKey} columns:`, taskColumns, 'from:', 
          this.taskColumnSelections.has(task.taskId) ? 'task-specific' : 'goal-level');
        
        // Match API structure exactly
        return {
          task_id: task.taskKey, // Use taskKey (e.g., "clean.whitespace") not taskId (GUID)
          params: params, // Use 'params' not 'parameters'
          select: {
            columns: taskColumns // Nested structure: select.columns
          }
        };
      });

    console.log('formData.targetColumns:', this.formData.targetColumns);
    console.log('tasksToSave:', tasksToSave);

    // Use the effective target columns for the goal
    const effectiveTargetColumns = this.getEffectiveTargetColumns();

    if (this.isEditMode) {
      // Update existing goal - use PascalCase for top-level properties
      const goalUpdate: any = {
        GoalId: this.effectiveData.goalData?.goalId,
        GoalCatalogId: this.selectedGoalId, // Save the template ID
        GoalName: this.formData.name || 'Untitled Goal',
        Description: this.formData.description,
        IsActive: this.formData.isActive !== undefined ? this.formData.isActive : true,
        Steps: tasksToSave
      };
      this.dataService.updateGoal(goalUpdate).subscribe({
        next: (success: boolean) => {
          this.loading = false;
          if (success) {
            // Reset initial state after successful save
            this.captureInitialState();
            
            if (this.isInlineMode) {
              this.inlineSubmit.emit({ data: this.formData, goalTasks: this.goalTasks });
            } else {
              this.dialogRef.close({ 
                success: true,
                updated: true,
                data: this.formData,
                goalUpdate: goalUpdate
              });
            }
          } else {
            this.error = 'Failed to update goal';
            this.snackBar.open('Failed to update goal', '×', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
          }
        },
        error: (error) => {
          this.loading = false;
          this.error = error.message || 'Failed to update goal';
          // Don't display error message for 402 Payment Required - already handled by interceptor
          if (error.status !== 402) {
            this.snackBar.open(error.message || 'Failed to update goal', '×', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
          }
        }
      });
    } else {
      // Create new goal - use PascalCase for top-level properties
      // Ensure the goal name is unique before creating
      const uniqueGoalName = this.generateUniqueGoalName(this.formData.name || 'Untitled Goal');
      if (uniqueGoalName !== this.formData.name) {
        // Update the form data with the unique name
        this.formData.name = uniqueGoalName;
      }
      
      const goalCreate: any = {
        ProjectId: this.effectiveData.projectId,
        GoalCatalogId: this.selectedGoalId, // Save the template ID
        GoalName: uniqueGoalName,
        Description: this.formData.description,
        IsActive: this.formData.isActive !== undefined ? this.formData.isActive : true,
        Order: this.effectiveData.initialOrder,
        Steps: tasksToSave
      };
      this.dataService.createGoal(goalCreate).subscribe({
        next: (goalId: string) => {
          this.loading = false;
          // Reset initial state after successful save
          this.captureInitialState();
          
          if (this.isInlineMode) {
            this.inlineSubmit.emit({ data: this.formData, goalTasks: this.goalTasks });
          } else {
            this.dialogRef.close({ 
              success: true,
              created: true,
              goalId: goalId,
              data: this.formData,
              goalCreate: goalCreate
            });
          }
        },
        error: (error) => {
          this.loading = false;
          this.error = error.message || 'Failed to create goal';
          // Don't display error message for 402 Payment Required - already handled by interceptor
          if (error.status !== 402) {
            this.snackBar.open(error.message || 'Failed to create goal', '×', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
          }
        }
      });
    }
  }

  /**
   * Check if a goal is supported by the user's plan or availability settings
   * @param goalKey The goal key to check
   * @returns True if the goal is available, false if upgrade required
   */
  isGoalSupported(goalKey: string): boolean {
    const goal = this.goalCatalog.find(g => g.goalKey === goalKey || (g as any).GoalKey === goalKey);
    if (!goal) {
      return true; // If not found in catalog, assume available
    }

    // If backend marked goal as unavailable, require upgrade
    if (goal.isAvailable === false) {
      return false;
    }

    // For future plan-based restrictions, could add additional checks here
    // if (!goal.planIds || goal.planIds.length === 0 || !this.userPlanId) {
    //   return true;
    // }
    // return goal.planIds.includes(this.userPlanId);

    return true;
  }

  getDialogTitle(): string {
    if (this.currentStage === 'goal-configuration') {
      // Show goal name and description in the header
      const taskName = this.formData.name || this.getSelectedGoalTemplate()?.metadata?.name || this.formData.goalType;
      const description = this.formData.description || this.getSelectedGoalTemplate()?.metadata?.description || 'No description available';
      return taskName;
    }
    return this.data?.title || 'Select Goal';
  }
  
  getDialogSubtitle(): string {
    if (this.currentStage === 'goal-configuration') {
      return this.formData.description || this.getSelectedGoalTemplate()?.metadata?.description || 'No description available';
    }
    if (this.currentStage === 'goal-selection') {
      return 'Choose a goal template to add to your project';
    }
    return '';
  }

  getConfirmButtonText(): string {
    if (this.loading) {
      return this.isEditMode ? 'Updating...' : 'Creating...';
    }
    return this.isEditMode ? 'Update' : 'Create';
  }

  // Handle scroll events for gradient effects
  onGoalsContentScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;

    // Threshold for showing/hiding gradients
    const threshold = 10;

    // Update scroll state
    this.hasScrollTop = scrollTop > threshold;
    this.hasScrollBottom = scrollBottom > threshold;
  }
}
